import React, { useEffect, useState } from 'react';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

import { ResetCurrentDynamic as BLResetCurrentDynamic, SetAutozoneState as BLSetAutozoneState } from './../../../utilities/BL/Zones';
import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2';

const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#795548'];

const VotingResultsChart = ({ data }) => (
    <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="votes" fill="#8884d8">
                {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
                ))}
            </Bar>
        </BarChart>
    </ResponsiveContainer>
);

const Voting = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    const [votingTitle, setVotingTitle] = useState('¿Quién narró mejor el gol del milagro?');
    const [option1Name, setOption1Name] = useState('Abonad@ 1');
    const [option2Name, setOption2Name] = useState('Abonad@ 2');
    
    const [gameStarted, setGameStarted] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [votingResults, setVotingResults] = useState([]);
    
    const [isSystemWorking, setIsSystemWorking] = useState(false);
    const [hasRoomBeenOpened, setHasRoomBeenOpened] = useState(false);

    // ==========================================
    // ESTADO Y EFECTO PARA ESTADÍSTICAS EN VIVO
    // ==========================================
    const [liveStadiumStats, setLiveStadiumStats] = useState(null);

    useEffect(() => {
        const fetchStadiumLiveStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
                if (response.ok) setLiveStadiumStats(await response.json());
            } catch (error) { }
        };
        
        fetchStadiumLiveStats(); // Llamada inicial
        const pollingInterval = setInterval(fetchStadiumLiveStats, 3000); // Se actualiza cada 3 seg
        
        return () => clearInterval(pollingInterval);
    }, []);

    const ReturnToDashboard = async () => {
        try {
            await SetScreenV2MainDynamic(1, null);
            await BLResetCurrentDynamic();
            getOutOfTheDynamic();
        } catch (error) { }
    };

    // FASE 1: PREPARAR SALA (Modo Automático)
    const handleOpenRoomAndShowQR = async () => {
        if (!votingTitle.trim() || !option1Name.trim() || !option2Name.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: "Llena la pregunta y las opciones." });
            return;
        }

        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Forzando cierre de sesiones y limpiando..." });

            // 1. ZONAS AUTOMÁTICAS
            await BLSetAutozoneState(true);
            await BLResetCurrentDynamic(); 
            await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, { method: 'POST' });
            
            // Pausa táctica para matar zombis
            await new Promise(resolve => setTimeout(resolve, 1500));

            await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roundName: `Votación: ${votingTitle}` })
            });

            await SetScreenV2MainDynamic(1, null);
            setHasRoomBeenOpened(true);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Sala abierta. Los aficionados ya pueden escanear el QR." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al preparar la sala." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // FASE 2: INICIAR VOTACIÓN
    const StartVoting = async () => {
        if (!votingTitle.trim() || !option1Name.trim() || !option2Name.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: "Llena la pregunta y las opciones antes de iniciar." });
            return;
        }

        try {
            setIsSystemWorking(true);

            // 1. Guardamos la configuración en C# JUSTO ANTES de iniciar (Por si la editaron por un empate)
            const configPayload = {
                question: votingTitle,
                options: [
                    { id: 1, text: option1Name, zoneRef: 'azul' },
                    { id: 2, text: option2Name, zoneRef: 'oro' }
                ]
            };
            await fetch(`${process.env.REACT_APP_API_URL}Voting/Setup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(configPayload)
            });

            // 2. Disparamos la votación
            const response = await fetch(`${process.env.REACT_APP_API_URL}Voting/Start`, { method: 'POST' });
            const data = await response.json();
            
            if (data.success) {
                setGameStarted(true);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Votación enviada a celulares y pantalla.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al iniciar votación." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // FASE 3: MOSTRAR RESULTADOS
    const ShowResultsView = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}Voting/Results`);
            const data = await res.json();
            
            if (data.success) {
                setVotingResults(data.data);
                setShowResults(true);
                await fetch(`${process.env.REACT_APP_API_URL}Voting/ShowGraph`, { method: 'POST' });
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Resultados enviados a la pantalla.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al obtener resultados." });
        }
    };

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <section className='AdmRunToGoal__Settings pb-5'>
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
                    <p className='AdmRunToGoal__Title mb-0'><i className='bi bi-bar-chart-steps me-2'></i> Votación en Vivo</p>
                    <div className="alert alert-dark py-2 px-3 mb-0 fw-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
                        Status: {showResults ? 'Mostrando Resultados' : gameStarted ? 'Votación Activa' : 'Configurando'}
                    </div>
                </div>

                {showResults ? (
                    <div className="bg-white p-4 rounded shadow-sm border">
                        <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
                            <h4 className="fw-bold text-primary mb-0"><i className="bi bi-trophy-fill me-2"></i> Resultados Oficiales</h4>
                            
                            {/* BOTÓN DE SOFT RESET (Empates / Nueva Votación) */}
                            <button className="btn btn-outline-secondary btn-sm fw-bold" onClick={async () => {
                                // 1. Borramos los votos en C#
                                await fetch(`${process.env.REACT_APP_API_URL}Voting/Reset`, { method: 'POST' });
                                
                                // 2. Mandamos a la gente al descanso SIN desconectarlos
                                await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, { method: 'POST' });
                                
                                // 3. Limpiamos Súper Lienzo
                                await SetScreenV2MainDynamic(1, null);

                                // 4. Volvemos al estado de edición
                                setShowResults(false);
                                setGameStarted(false);
                                // Nota: NO apagamos hasRoomBeenOpened para que la gente siga dentro.
                                
                                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Votación limpia. Modifica las opciones y vuelve a iniciar." });
                            }}>
                                <i className="bi bi-arrow-counterclockwise me-1"></i> Nueva Votación (Misma Sala)
                            </button>
                        </div>
                        
                        <h5 className="text-center fw-bold text-dark mb-4">"{votingTitle}"</h5>
                        <VotingResultsChart data={votingResults} />
                        
                        <div className="row mt-4">
                            {votingResults.map((result, index) => (
                                <div key={index} className="col-md-6 mb-3">
                                    <div className="card border-0 shadow-sm bg-light">
                                        <div className="card-body text-center">
                                            <h5 className="fw-bold text-uppercase text-muted">{result.name}</h5>
                                            <h2 className="fw-black mb-0 text-dark">{result.votes} Votos</h2>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="row g-4">
                        <div className="col-md-7 mb-4">
                            <div className="card shadow-sm h-100 border-dark">
                                <div className="card-header bg-dark text-white p-3 fw-bold">1. Editor de Votación</div>
                                <div className="card-body bg-light p-4">
                                    
                                    {/* ALERTA DE EMPATE (Solo visible si la sala ya está abierta y no hay juego activo) */}
                                    {hasRoomBeenOpened && !gameStarted && (
                                        <div className="alert alert-info py-2 text-center small fw-bold mb-4">
                                            <i className="bi bi-info-circle-fill me-2"></i> 
                                            La sala está abierta. Puedes editar los nombres para una ronda de desempate.
                                        </div>
                                    )}

                                    <div className="mb-4">
                                        <label className="fw-bold text-primary mb-2">Pregunta en Pantalla</label>
                                        <input type="text" className="form-control form-control-lg fw-bold" placeholder="Ej. ¿Quién narró mejor?" value={votingTitle} onChange={(e) => setVotingTitle(e.target.value)} disabled={gameStarted} />
                                    </div>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="fw-bold text-muted mb-2">Opción 1</label>
                                            <input type="text" className="form-control border-primary" style={{ borderLeftWidth: '5px' }} value={option1Name} onChange={(e) => setOption1Name(e.target.value)} disabled={gameStarted} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="fw-bold text-muted mb-2">Opción 2</label>
                                            <input type="text" className="form-control border-warning" style={{ borderLeftWidth: '5px' }} value={option2Name} onChange={(e) => setOption2Name(e.target.value)} disabled={gameStarted} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-md-5 mb-4">
                            <div className="card shadow-sm h-100 border-primary">
                                <div className="card-header bg-primary text-white fw-bold">2. Controles de Operación</div>
                                <div className="card-body bg-light">
                                    <button className={`btn btn-danger fw-bold w-100 py-3 mb-4 shadow-sm ${isSystemWorking ? 'disabled' : ''}`} onClick={handleOpenRoomAndShowQR} disabled={isSystemWorking}>
                                        <i className="bi bi-qr-code me-1"></i> {hasRoomBeenOpened ? 'Purgar Sala y Reabrir' : 'Mostrar QR y Abrir Sala'}
                                    </button>
                                    
                                    {!gameStarted ? (
                                        <button className={`btn btn-success mb-3 w-100 py-3 fw-bold shadow-sm ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`} onClick={StartVoting} disabled={!hasRoomBeenOpened || isSystemWorking}>
                                            <i className="bi bi-play-fill me-1"></i> Iniciar Votación
                                        </button>
                                    ) : (
                                        <button className="btn btn-dark w-100 py-3 fw-bold shadow-sm mt-2" onClick={ShowResultsView}>
                                            <i className="bi bi-bar-chart-fill me-1"></i> Mostrar Resultados
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* =======================================================
                    ESTADÍSTICAS EN VIVO
                ======================================================= */}
                <div className='mt-3 mb-5 border-top pt-4'>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">Estadísticas de Votación en Vivo</h5>
                        {liveStadiumStats && <span className="badge bg-secondary">{liveStadiumStats.roundName}</span>}
                    </div>

                    {liveStadiumStats ? (
                        <>
                            <div className="row text-center mb-4 g-3">
                                <div className="col-sm-6">
                                    <div className="p-4 bg-light rounded border border-success border-opacity-25 shadow-sm">
                                        <h1 className="fw-black text-success mb-0" style={{ fontSize: '3rem' }}>{liveStadiumStats.totalCurrentlyActive}</h1>
                                        <span className="fw-bold text-muted text-uppercase">Votantes Conectados</span>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="p-4 bg-light rounded border shadow-sm">
                                        <h2 className="fw-black text-secondary mb-0">{liveStadiumStats.totalEverConnected}</h2>
                                        <span className="small text-muted text-uppercase">Total de Escaneos</span>
                                    </div>
                                </div>
                            </div>

                            {/* TABLA DETALLADA */}
                            {liveStadiumStats.zones && Object.keys(liveStadiumStats.zones).length > 0 && (
                                <div className="table-responsive mb-4 mt-4">
                                    <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="text-start ps-3">OPCIÓN / EQUIPO</th>
                                                <th>VOTANTES ACTIVOS</th>
                                                <th>PICO MÁXIMO</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(liveStadiumStats.zones).map(([zoneKey, stats], i) => {
                                                let visualName = zoneKey.toUpperCase();
                                                if (zoneKey.toLowerCase() === 'azul') visualName = option1Name || 'Opción 1';
                                                if (zoneKey.toLowerCase() === 'oro') visualName = option2Name || 'Opción 2';

                                                if (stats.active === 0 && stats.peak === 0) return null;

                                                return (
                                                    <tr key={i}>
                                                        <td className="text-start ps-3 fw-bold text-uppercase">{visualName}</td>
                                                        <td className="fs-6 text-success fw-bold">{stats.active}</td>
                                                        <td className="text-muted">{stats.peak}</td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center p-4 text-muted">
                            <div className="spinner-border mb-3" role="status"></div>
                            <p>Cargando datos del estadio...</p>
                        </div>
                    )}
                </div>
            </section>
        </SecondaryBase>
    );
};

export { Voting };









// Funcional
// import React, { useEffect, useState } from 'react';
// import { SecondaryBase } from './../Commons/SecondaryBase';
// import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
// import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// import { ResetCurrentDynamic as BLResetCurrentDynamic, SetAutozoneState as BLSetAutozoneState } from './../../../utilities/BL/Zones';
// import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2';

// const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#795548'];

// const VotingResultsChart = ({ data }) => (
//     <ResponsiveContainer width="100%" height={300}>
//         <BarChart data={data}>
//             <XAxis dataKey="name" />
//             <YAxis />
//             <Tooltip />
//             <Bar dataKey="votes" fill="#8884d8">
//                 {data.map((entry, index) => (
//                     <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
//                 ))}
//             </Bar>
//         </BarChart>
//     </ResponsiveContainer>
// );

// const Voting = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage();

//     const [votingTitle, setVotingTitle] = useState('¿Quién narró mejor?');
//     const [option1Name, setOption1Name] = useState('María');
//     const [option2Name, setOption2Name] = useState('Juan');
    
//     const [gameStarted, setGameStarted] = useState(false);
//     const [showResults, setShowResults] = useState(false);
//     const [votingResults, setVotingResults] = useState([]);
    
//     const [isSystemWorking, setIsSystemWorking] = useState(false);
//     const [hasRoomBeenOpened, setHasRoomBeenOpened] = useState(false);

//     // ==========================================
//     // ESTADO Y EFECTO PARA ESTADÍSTICAS EN VIVO
//     // ==========================================
//     const [liveStadiumStats, setLiveStadiumStats] = useState(null);

//     useEffect(() => {
//         const fetchStadiumLiveStats = async () => {
//             try {
//                 const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
//                 if (response.ok) setLiveStadiumStats(await response.json());
//             } catch (error) { }
//         };
        
//         fetchStadiumLiveStats(); // Llamada inicial
//         const pollingInterval = setInterval(fetchStadiumLiveStats, 3000); // Se actualiza cada 3 seg
        
//         return () => clearInterval(pollingInterval);
//     }, []);

//     const ReturnToDashboard = async () => {
//         try {
//             await SetScreenV2MainDynamic(1, null);
//             await BLResetCurrentDynamic();
//             getOutOfTheDynamic();
//         } catch (error) { }
//     };

//     // FASE 1: PREPARAR SALA (Modo Automático)
//     const handleOpenRoomAndShowQR = async () => {
//         if (!votingTitle.trim() || !option1Name.trim() || !option2Name.trim()) {
//             showMessage({ type: MESSAGE_TYPE.WARNING, text: "Llena la pregunta y las opciones." });
//             return;
//         }

//         try {
//             setIsSystemWorking(true);
//             showMessage({ type: MESSAGE_TYPE.INFO, text: "Forzando cierre de sesiones y limpiando..." });

//             // 1. ZONAS AUTOMÁTICAS (Para que entren directo a esperar la votación)
//             await BLSetAutozoneState(true);
//             await BLResetCurrentDynamic(); 
//             await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, { method: 'POST' });
            
//             // Pausa táctica para matar zombis
//             await new Promise(resolve => setTimeout(resolve, 1500));

//             await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ roundName: `Votación: ${votingTitle}` })
//             });

//             // Guardamos en la memoria del servidor
//             const configPayload = {
//                 question: votingTitle,
//                 options: [
//                     { id: 1, text: option1Name, zoneRef: 'azul' },
//                     { id: 2, text: option2Name, zoneRef: 'oro' }
//                 ]
//             };
//             await fetch(`${process.env.REACT_APP_API_URL}Voting/Setup`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify(configPayload)
//             });

//             await SetScreenV2MainDynamic(1, null);
//             setHasRoomBeenOpened(true);
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Configuración guardada y Sala abierta." });
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al preparar la sala." });
//         } finally {
//             setIsSystemWorking(false);
//         }
//     };

//     // FASE 2: INICIAR VOTACIÓN
//     const StartVoting = async () => {
//         try {
//             setIsSystemWorking(true);
//             const response = await fetch(`${process.env.REACT_APP_API_URL}Voting/Start`, { method: 'POST' });
//             const data = await response.json();
            
//             if (data.success) {
//                 setGameStarted(true);
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Votación enviada a celulares y pantalla.' });
//             }
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al iniciar votación." });
//         } finally {
//             setIsSystemWorking(false);
//         }
//     };

//     // FASE 3: MOSTRAR RESULTADOS
//     const ShowResultsView = async () => {
//         try {
//             const res = await fetch(`${process.env.REACT_APP_API_URL}Voting/Results`);
//             const data = await res.json();
            
//             if (data.success) {
//                 setVotingResults(data.data);
//                 setShowResults(true);
//                 await fetch(`${process.env.REACT_APP_API_URL}Voting/ShowGraph`, { method: 'POST' });
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Resultados enviados a la pantalla.' });
//             }
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al obtener resultados." });
//         }
//     };

//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             <section className='AdmRunToGoal__Settings pb-5'>
//                 <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
//                     <p className='AdmRunToGoal__Title mb-0'><i className='bi bi-bar-chart-steps me-2'></i> Votación en Vivo</p>
//                     <div className="alert alert-dark py-2 px-3 mb-0 fw-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
//                         Status: {gameStarted ? 'Votación Activa' : 'Configurando'}
//                     </div>
//                 </div>

//                 {showResults ? (
//                     <div className="bg-white p-4 rounded shadow-sm border">
//                         <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
//                             <h4 className="fw-bold text-primary mb-0"><i className="bi bi-trophy-fill me-2"></i> Resultados Oficiales</h4>
//                             {/* <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowResults(false)}>Volver</button> */}

//                             <button className="btn btn-outline-secondary btn-sm" onClick={async () => {
//                                 // Le avisamos al servidor que borre la pregunta anterior
//                                 await fetch(`${process.env.REACT_APP_API_URL}Voting/Reset`, { method: 'POST' });
                                
//                                 // Escondemos los resultados y limpiamos la vista
//                                 setShowResults(false);
//                                 setGameStarted(false);
//                                 setHasRoomBeenOpened(false);
//                                 setVotingTitle('');
//                                 setOption1Name('');
//                                 setOption2Name('');
                                
//                                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Votación reseteada. Puedes iniciar una nueva." });
//                             }}>Volver y Nueva Votación</button>
//                         </div>
//                         <h5 className="text-center fw-bold text-dark mb-4">"{votingTitle}"</h5>
//                         <VotingResultsChart data={votingResults} />
//                         <div className="row mt-4">
//                             {votingResults.map((result, index) => (
//                                 <div key={index} className="col-md-6 mb-3">
//                                     <div className="card border-0 shadow-sm bg-light">
//                                         <div className="card-body text-center">
//                                             <h5 className="fw-bold text-uppercase text-muted">{result.name}</h5>
//                                             <h2 className="fw-black mb-0 text-dark">{result.votes} Votos</h2>
//                                         </div>
//                                     </div>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 ) : (
//                     <div className="row g-4">
//                         <div className="col-md-7 mb-4">
//                             <div className="card shadow-sm h-100 border-dark">
//                                 <div className="card-header bg-dark text-white p-3 fw-bold">1. Editor de Votación</div>
//                                 <div className="card-body bg-light p-4">
//                                     <div className="mb-4">
//                                         <label className="fw-bold text-primary mb-2">Pregunta en Pantalla</label>
//                                         <input type="text" className="form-control form-control-lg fw-bold" placeholder="Ej. ¿Quién narró mejor?" value={votingTitle} onChange={(e) => setVotingTitle(e.target.value)} disabled={hasRoomBeenOpened} />
//                                     </div>
//                                     <div className="row g-3">
//                                         <div className="col-md-6">
//                                             <label className="fw-bold text-muted mb-2">Opción 1</label>
//                                             <input type="text" className="form-control border-primary" style={{ borderLeftWidth: '5px' }} value={option1Name} onChange={(e) => setOption1Name(e.target.value)} disabled={hasRoomBeenOpened} />
//                                         </div>
//                                         <div className="col-md-6">
//                                             <label className="fw-bold text-muted mb-2">Opción 2</label>
//                                             <input type="text" className="form-control border-warning" style={{ borderLeftWidth: '5px' }} value={option2Name} onChange={(e) => setOption2Name(e.target.value)} disabled={hasRoomBeenOpened} />
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>

//                         <div className="col-md-5 mb-4">
//                             <div className="card shadow-sm h-100 border-primary">
//                                 <div className="card-header bg-primary text-white fw-bold">2. Controles de Operación</div>
//                                 <div className="card-body bg-light">
//                                     <button className={`btn btn-danger fw-bold w-100 py-3 mb-4 shadow-sm ${isSystemWorking ? 'disabled' : ''}`} onClick={handleOpenRoomAndShowQR} disabled={isSystemWorking}>
//                                         <i className="bi bi-qr-code me-1"></i> Mostrar QR y Abrir Sala
//                                     </button>
                                    
//                                     {!gameStarted ? (
//                                         <button className={`btn btn-success mb-3 w-100 py-3 fw-bold shadow-sm ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`} onClick={StartVoting} disabled={!hasRoomBeenOpened || isSystemWorking}>
//                                             <i className="bi bi-play-fill me-1"></i> Iniciar Votación
//                                         </button>
//                                     ) : (
//                                         <button className="btn btn-dark w-100 py-3 fw-bold shadow-sm mt-2" onClick={ShowResultsView}>
//                                             <i className="bi bi-bar-chart-fill me-1"></i> Mostrar Resultados
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 )}

//                 {/* =======================================================
//                     ESTADÍSTICAS EN VIVO (AHORA VISIBLES SIEMPRE)
//                 ======================================================= */}
//                 <div className='mt-3 mb-5 border-top pt-4'>
//                     <div className="d-flex justify-content-between align-items-center mb-3">
//                         <h5 className="fw-bold mb-0">Estadísticas de Votación en Vivo</h5>
//                         {liveStadiumStats && <span className="badge bg-secondary">{liveStadiumStats.roundName}</span>}
//                     </div>

//                     {liveStadiumStats ? (
//                         <>
//                             <div className="row text-center mb-4 g-3">
//                                 <div className="col-sm-6">
//                                     <div className="p-4 bg-light rounded border border-success border-opacity-25 shadow-sm">
//                                         <h1 className="fw-black text-success mb-0" style={{ fontSize: '3rem' }}>{liveStadiumStats.totalCurrentlyActive}</h1>
//                                         <span className="fw-bold text-muted text-uppercase">Votantes Conectados</span>
//                                     </div>
//                                 </div>
//                                 <div className="col-sm-6">
//                                     <div className="p-4 bg-light rounded border shadow-sm">
//                                         <h2 className="fw-black text-secondary mb-0">{liveStadiumStats.totalEverConnected}</h2>
//                                         <span className="small text-muted text-uppercase">Total de Escaneos</span>
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* TABLA DETALLADA (Mapeando Azul/Oro a los nombres reales) */}
//                             {liveStadiumStats.zones && Object.keys(liveStadiumStats.zones).length > 0 && (
//                                 <div className="table-responsive mb-4 mt-4">
//                                     <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
//                                         <thead className="table-light">
//                                             <tr>
//                                                 <th className="text-start ps-3">OPCIÓN / EQUIPO</th>
//                                                 <th>VOTANTES ACTIVOS</th>
//                                                 <th>PICO MÁXIMO</th>
//                                             </tr>
//                                         </thead>
//                                         <tbody>
//                                             {Object.entries(liveStadiumStats.zones).map(([zoneKey, stats], i) => {
//                                                 // Traductor visual: El backend sigue asignando zonas, nosotros las disfrazamos.
//                                                 let visualName = zoneKey.toUpperCase();
//                                                 if (zoneKey.toLowerCase() === 'azul') visualName = option1Name || 'Opción 1';
//                                                 if (zoneKey.toLowerCase() === 'oro') visualName = option2Name || 'Opción 2';

//                                                 // Para que no se muestren las zonas vacías si no estás usándolas
//                                                 if (stats.active === 0 && stats.peak === 0) return null;

//                                                 return (
//                                                     <tr key={i}>
//                                                         <td className="text-start ps-3 fw-bold text-uppercase">{visualName}</td>
//                                                         <td className="fs-6 text-success fw-bold">{stats.active}</td>
//                                                         <td className="text-muted">{stats.peak}</td>
//                                                     </tr>
//                                                 )
//                                             })}
//                                         </tbody>
//                                     </table>
//                                 </div>
//                             )}
//                         </>
//                     ) : (
//                         <div className="text-center p-4 text-muted">
//                             <div className="spinner-border mb-3" role="status"></div>
//                             <p>Cargando datos del estadio...</p>
//                         </div>
//                     )}
//                 </div>
//             </section>
//         </SecondaryBase>
//     );
// };

// export { Voting };









// import React, { useEffect, useState } from 'react';
// import { SecondaryBase } from './../Commons/SecondaryBase';
// import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
// import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// import { ResetCurrentDynamic as BLResetCurrentDynamic, SetAutozoneState as BLSetAutozoneState } from './../../../utilities/BL/Zones';
// import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2';

// const barColors = ['#f44336', '#2196f3', '#4caf50', '#ff9800', '#9c27b0', '#00bcd4', '#e91e63', '#795548'];

// const VotingResultsChart = ({ data }) => (
//     <ResponsiveContainer width="100%" height={300}>
//         <BarChart data={data}>
//             <XAxis dataKey="name" />
//             <YAxis />
//             <Tooltip />
//             <Bar dataKey="votes" fill="#8884d8">
//                 {data.map((entry, index) => (
//                     <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
//                 ))}
//             </Bar>
//         </BarChart>
//     </ResponsiveContainer>
// );

// const Voting = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage();

//     const [votingTitle, setVotingTitle] = useState('¿Quién narró mejor?');
//     const [option1Name, setOption1Name] = useState('María');
//     const [option2Name, setOption2Name] = useState('Juan');
    
//     const [gameStarted, setGameStarted] = useState(false);
//     const [showResults, setShowResults] = useState(false);
//     const [votingResults, setVotingResults] = useState([]);
    
//     const [isSystemWorking, setIsSystemWorking] = useState(false);
//     const [hasRoomBeenOpened, setHasRoomBeenOpened] = useState(false);

//     // ==========================================
//     // ESTADO Y EFECTO PARA ESTADÍSTICAS EN VIVO
//     // ==========================================
//     const [liveStadiumStats, setLiveStadiumStats] = useState(null);

//     useEffect(() => {
//         const fetchStadiumLiveStats = async () => {
//             try {
//                 const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
//                 if (response.ok) setLiveStadiumStats(await response.json());
//             } catch (error) { }
//         };
        
//         fetchStadiumLiveStats(); // Llamada inicial
//         const pollingInterval = setInterval(fetchStadiumLiveStats, 3000); // Se actualiza cada 3 seg
        
//         return () => clearInterval(pollingInterval);
//     }, []);

//     const ReturnToDashboard = async () => {
//         try {
//             await SetScreenV2MainDynamic(1, null);
//             await BLResetCurrentDynamic();
//             getOutOfTheDynamic();
//         } catch (error) { }
//     };

//     // FASE 1: PREPARAR SALA (Modo Automático)
//     const handleOpenRoomAndShowQR = async () => {
//         if (!votingTitle.trim() || !option1Name.trim() || !option2Name.trim()) {
//             showMessage({ type: MESSAGE_TYPE.WARNING, text: "Llena la pregunta y las opciones." });
//             return;
//         }

//         try {
//             setIsSystemWorking(true);
//             showMessage({ type: MESSAGE_TYPE.INFO, text: "Forzando cierre de sesiones y limpiando..." });

//             // 1. ZONAS AUTOMÁTICAS (Para que entren directo a esperar la votación)
//             await BLSetAutozoneState(true);
//             await BLResetCurrentDynamic(); 
//             await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, { method: 'POST' });
            
//             // Pausa táctica para matar zombis
//             await new Promise(resolve => setTimeout(resolve, 1500));

//             await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify({ roundName: `Votación: ${votingTitle}` })
//             });

//             // Guardamos en la memoria del servidor
//             const configPayload = {
//                 question: votingTitle,
//                 options: [
//                     { id: 1, text: option1Name, zoneRef: 'azul' },
//                     { id: 2, text: option2Name, zoneRef: 'oro' }
//                 ]
//             };
//             await fetch(`${process.env.REACT_APP_API_URL}Voting/Setup`, {
//                 method: 'POST',
//                 headers: { 'Content-Type': 'application/json' },
//                 body: JSON.stringify(configPayload)
//             });

//             await SetScreenV2MainDynamic(1, null);
//             setHasRoomBeenOpened(true);
//             showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Configuración guardada y Sala abierta." });
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al preparar la sala." });
//         } finally {
//             setIsSystemWorking(false);
//         }
//     };

//     // FASE 2: INICIAR VOTACIÓN
//     const StartVoting = async () => {
//         try {
//             setIsSystemWorking(true);
//             const response = await fetch(`${process.env.REACT_APP_API_URL}Voting/Start`, { method: 'POST' });
//             const data = await response.json();
            
//             if (data.success) {
//                 setGameStarted(true);
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Votación enviada a celulares y pantalla.' });
//             }
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al iniciar votación." });
//         } finally {
//             setIsSystemWorking(false);
//         }
//     };

//     // FASE 3: MOSTRAR RESULTADOS
//     const ShowResultsView = async () => {
//         try {
//             const res = await fetch(`${process.env.REACT_APP_API_URL}Voting/Results`);
//             const data = await res.json();
            
//             if (data.success) {
//                 setVotingResults(data.data);
//                 setShowResults(true);
//                 await fetch(`${process.env.REACT_APP_API_URL}Voting/ShowGraph`, { method: 'POST' });
//                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Resultados enviados a la pantalla.' });
//             }
//         } catch (error) {
//             showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al obtener resultados." });
//         }
//     };

//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             <section className='AdmRunToGoal__Settings pb-5'>
//                 <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
//                     <p className='AdmRunToGoal__Title mb-0'><i className='bi bi-bar-chart-steps me-2'></i> Votación en Vivo</p>
//                     <div className="alert alert-dark py-2 px-3 mb-0 fw-bold shadow-sm" style={{ fontSize: '0.9rem' }}>
//                         Status: {gameStarted ? 'Votación Activa' : 'Configurando'}
//                     </div>
//                 </div>

//                 {showResults ? (
//                     <div className="bg-white p-4 rounded shadow-sm border">
//                         <div className="d-flex justify-content-between align-items-center border-bottom pb-3 mb-4">
//                             <h4 className="fw-bold text-primary mb-0"><i className="bi bi-trophy-fill me-2"></i> Resultados Oficiales</h4>
//                             {/* <button className="btn btn-outline-secondary btn-sm" onClick={() => setShowResults(false)}>Volver</button> */}

//                             <button className="btn btn-outline-secondary btn-sm" onClick={async () => {
//                                 // Le avisamos al servidor que borre la pregunta anterior
//                                 await fetch(`${process.env.REACT_APP_API_URL}Voting/Reset`, { method: 'POST' });
                                
//                                 // Escondemos los resultados y limpiamos la vista
//                                 setShowResults(false);
//                                 setGameStarted(false);
//                                 setHasRoomBeenOpened(false);
//                                 setVotingTitle('');
//                                 setOption1Name('');
//                                 setOption2Name('');
                                
//                                 showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Votación reseteada. Puedes iniciar una nueva." });
//                             }}>Volver y Nueva Votación</button>
//                         </div>
//                         <h5 className="text-center fw-bold text-dark mb-4">"{votingTitle}"</h5>
//                         <VotingResultsChart data={votingResults} />
//                         <div className="row mt-4">
//                             {votingResults.map((result, index) => (
//                                 <div key={index} className="col-md-6 mb-3">
//                                     <div className="card border-0 shadow-sm bg-light">
//                                         <div className="card-body text-center">
//                                             <h5 className="fw-bold text-uppercase text-muted">{result.name}</h5>
//                                             <h2 className="fw-black mb-0 text-dark">{result.votes} Votos</h2>
//                                         </div>
//                                     </div>
//                                 </div>
//                             ))}
//                         </div>
//                     </div>
//                 ) : (
//                     <div className="row g-4">
//                         <div className="col-md-7 mb-4">
//                             <div className="card shadow-sm h-100 border-dark">
//                                 <div className="card-header bg-dark text-white p-3 fw-bold">1. Editor de Votación</div>
//                                 <div className="card-body bg-light p-4">
//                                     <div className="mb-4">
//                                         <label className="fw-bold text-primary mb-2">Pregunta en Pantalla</label>
//                                         <input type="text" className="form-control form-control-lg fw-bold" placeholder="Ej. ¿Quién narró mejor?" value={votingTitle} onChange={(e) => setVotingTitle(e.target.value)} disabled={hasRoomBeenOpened} />
//                                     </div>
//                                     <div className="row g-3">
//                                         <div className="col-md-6">
//                                             <label className="fw-bold text-muted mb-2">Opción 1</label>
//                                             <input type="text" className="form-control border-primary" style={{ borderLeftWidth: '5px' }} value={option1Name} onChange={(e) => setOption1Name(e.target.value)} disabled={hasRoomBeenOpened} />
//                                         </div>
//                                         <div className="col-md-6">
//                                             <label className="fw-bold text-muted mb-2">Opción 2</label>
//                                             <input type="text" className="form-control border-warning" style={{ borderLeftWidth: '5px' }} value={option2Name} onChange={(e) => setOption2Name(e.target.value)} disabled={hasRoomBeenOpened} />
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         </div>

//                         <div className="col-md-5 mb-4">
//                             <div className="card shadow-sm h-100 border-primary">
//                                 <div className="card-header bg-primary text-white fw-bold">2. Controles de Operación</div>
//                                 <div className="card-body bg-light">
//                                     <button className={`btn btn-danger fw-bold w-100 py-3 mb-4 shadow-sm ${isSystemWorking ? 'disabled' : ''}`} onClick={handleOpenRoomAndShowQR} disabled={isSystemWorking}>
//                                         <i className="bi bi-qr-code me-1"></i> Mostrar QR y Abrir Sala
//                                     </button>
                                    
//                                     {!gameStarted ? (
//                                         <button className={`btn btn-success mb-3 w-100 py-3 fw-bold shadow-sm ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`} onClick={StartVoting} disabled={!hasRoomBeenOpened || isSystemWorking}>
//                                             <i className="bi bi-play-fill me-1"></i> Iniciar Votación
//                                         </button>
//                                     ) : (
//                                         <button className="btn btn-dark w-100 py-3 fw-bold shadow-sm mt-2" onClick={ShowResultsView}>
//                                             <i className="bi bi-bar-chart-fill me-1"></i> Mostrar Resultados
//                                         </button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                     </div>
//                 )}

//                 {/* =======================================================
//                     ESTADÍSTICAS EN VIVO
//                 ======================================================= */}
//                 {!showResults && (
//                     <div className='mt-3 mb-5 border-top pt-4'>
//                         <div className="d-flex justify-content-between align-items-center mb-3">
//                             <h5 className="fw-bold mb-0">Estadísticas de Votación en Vivo</h5>
//                             {liveStadiumStats && <span className="badge bg-secondary">{liveStadiumStats.roundName}</span>}
//                         </div>

//                         {liveStadiumStats ? (
//                             <>
//                                 <div className="row text-center mb-4 g-3">
//                                     <div className="col-sm-6">
//                                         <div className="p-4 bg-light rounded border border-success border-opacity-25 shadow-sm">
//                                             <h1 className="fw-black text-success mb-0" style={{ fontSize: '3rem' }}>{liveStadiumStats.totalCurrentlyActive}</h1>
//                                             <span className="fw-bold text-muted text-uppercase">Votantes Conectados</span>
//                                         </div>
//                                     </div>
//                                     <div className="col-sm-6">
//                                         <div className="p-4 bg-light rounded border shadow-sm">
//                                             <h2 className="fw-black text-secondary mb-0">{liveStadiumStats.totalEverConnected}</h2>
//                                             <span className="small text-muted text-uppercase">Total de Escaneos</span>
//                                         </div>
//                                     </div>
//                                 </div>

//                                 {/* TABLA DETALLADA (Mapeando Azul/Oro a los nombres reales) */}
//                                 {/* {liveStadiumStats.zoneStats && liveStadiumStats.zoneStats.length > 0 && (
//                                     <div className="table-responsive mb-4">
//                                         <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
//                                             <thead className="table-light">
//                                                 <tr>
//                                                     <th className="text-start ps-3">OPCIÓN / EQUIPO</th>
//                                                     <th>ACTIVOS</th>
//                                                     <th>ENTRARON</th>
//                                                     <th>SALIERON</th>
//                                                 </tr>
//                                             </thead>
//                                             <tbody>
//                                                 {liveStadiumStats.zoneStats.map((z, i) => {
//                                                     // Traductor visual: Si el sistema dice "azul", mostramos "María"
//                                                     let visualName = z.zone.toUpperCase();
//                                                     if (z.zone.toLowerCase() === 'azul') visualName = option1Name || 'Opción 1';
//                                                     if (z.zone.toLowerCase() === 'oro') visualName = option2Name || 'Opción 2';

//                                                     return (
//                                                         <tr key={i}>
//                                                             <td className="text-start ps-3 fw-bold text-uppercase">{visualName}</td>
//                                                             <td className="fs-6 text-success fw-bold">{z.currentActive}</td>
//                                                             <td>{z.totalEntered}</td>
//                                                             <td className="text-danger">{z.totalLeft}</td>
//                                                         </tr>
//                                                     )
//                                                 })}
//                                             </tbody>
//                                         </table>
//                                     </div>
//                                 )} */}

//                                 {/* TABLA DETALLADA (Mapeando Azul/Oro a los nombres reales) */}
//                                 {liveStadiumStats.zoneStats && liveStadiumStats.zoneStats.length > 0 && (
//                                     <div className="table-responsive mb-4">
//                                         <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
//                                             <thead className="table-light">
//                                                 <tr>
//                                                     <th className="text-start ps-3">OPCIÓN / EQUIPO</th>
//                                                     <th>VOTANTES ACTIVOS</th>
//                                                     <th>ENTRARON</th>
//                                                     <th>SALIERON</th>
//                                                 </tr>
//                                             </thead>
//                                             <tbody>
//                                                 {liveStadiumStats.zoneStats.map((z, i) => {
//                                                     // Traductor visual: El backend sigue asignando zonas, nosotros las disfrazamos.
//                                                     let visualName = z.zone.toUpperCase();
//                                                     if (z.zone.toLowerCase() === 'azul') visualName = option1Name || 'Opción 1';
//                                                     if (z.zone.toLowerCase() === 'oro') visualName = option2Name || 'Opción 2';

//                                                     return (
//                                                         <tr key={i}>
//                                                             <td className="text-start ps-3 fw-bold text-uppercase">{visualName}</td>
//                                                             <td className="fs-6 text-success fw-bold">{z.currentActive}</td>
//                                                             <td>{z.totalEntered}</td>
//                                                             <td className="text-danger">{z.totalLeft}</td>
//                                                         </tr>
//                                                     )
//                                                 })}
//                                             </tbody>
//                                         </table>
//                                     </div>
//                                 )}

//                                 {/* TABLA DETALLADA (Mapeando Azul/Oro a los nombres reales) */}
//                                 {liveStadiumStats.zones && Object.keys(liveStadiumStats.zones).length > 0 && (
//                                     <div className="table-responsive mb-4 mt-4">
//                                         <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
//                                             <thead className="table-light">
//                                                 <tr>
//                                                     <th className="text-start ps-3">OPCIÓN / EQUIPO</th>
//                                                     <th>VOTANTES ACTIVOS</th>
//                                                     <th>PICO MÁXIMO</th>
//                                                 </tr>
//                                             </thead>
//                                             <tbody>
//                                                 {Object.entries(liveStadiumStats.zones).map(([zoneKey, stats], i) => {
//                                                     // Traductor visual: El backend sigue asignando zonas, nosotros las disfrazamos.
//                                                     let visualName = zoneKey.toUpperCase();
//                                                     if (zoneKey.toLowerCase() === 'azul') visualName = option1Name || 'Opción 1';
//                                                     if (zoneKey.toLowerCase() === 'oro') visualName = option2Name || 'Opción 2';

//                                                     // Para que no se muestren las zonas vacías si no estás usándolas
//                                                     if (stats.active === 0 && stats.peak === 0) return null;

//                                                     return (
//                                                         <tr key={i}>
//                                                             <td className="text-start ps-3 fw-bold text-uppercase">{visualName}</td>
//                                                             <td className="fs-6 text-success fw-bold">{stats.active}</td>
//                                                             <td className="text-muted">{stats.peak}</td>
//                                                         </tr>
//                                                     )
//                                                 })}
//                                             </tbody>
//                                         </table>
//                                     </div>
//                                 )}
//                             </>
//                         ) : (
//                             <div className="text-center p-4 text-muted">
//                                 <div className="spinner-border mb-3" role="status"></div>
//                                 <p>Cargando datos del estadio...</p>
//                             </div>
//                         )}
//                     </div>
//                 )}
//             </section>
//         </SecondaryBase>
//     );
// };

// export { Voting };